import _ from 'lodash';
import React, {
	forwardRef,
	FunctionComponent,
	ReactNode,
	useRef,
	useState,
} from 'react';
import { NavTreeItem } from '../../../../logic/domains/navigationTree.domain';
import { getArrows } from '../../Common/Arrow/Arrow';
import navBarTestIds from '../NavigationMenu.elements';
import { AnchorNavLink, RouterNavLink } from '../NavLink/NavLink';
import SubMenu from '../SubMenu/SubMenu';
import * as Styled from './styled';

type MenuItemProps = {
	title: string;
	relativePath: string;
	absolutePath?: string;
	className?: string;
	subpages?: NavTreeItem[];
	activeStyle?: any;
	showSubMenuAlignedRight?: Function;
};

const IGNORE_ON_CLICK_OUTSIDE_CLASS = 'menu-item-sub-menu-dropdown';

const MenuItem: FunctionComponent<MenuItemProps> = ({
	title,
	relativePath,
	absolutePath,
	className = '',
	subpages,
	activeStyle,
	showSubMenuAlignedRight,
}: MenuItemProps) => {
	const hasSubMenu = subpages && subpages.length > 0;

	const listRef = useRef(null);

	const [subMenuVisible, setSubMenuVisibility] = useState(false);
	const customActiveStyle =
		hasSubMenu && subMenuVisible ? activeStyle : undefined;
	const parentId = _.kebabCase(title);
	const analyticsIdentifier = `nav-${parentId}`;

	function handleMenuClick(event: any) {
		if (hasSubMenu) {
			event && event.preventDefault();
			setSubMenuVisibility(!subMenuVisible);
		}
	}

	function hideSubMenu(event: any) {
		if (hasSubMenu) {
			setSubMenuVisibility(!subMenuVisible);
		}
	}

	return (
		<Styled.Li
			data-id={navBarTestIds.navMenuItemContainer}
			ref={listRef}
			id={parentId}
		>
			<NavigationLink
				title={title}
				className={className}
				relativePath={relativePath}
				absolutePath={absolutePath}
				handleMenuClick={handleMenuClick}
				hasSubMenu={hasSubMenu}
				subMenuVisible={subMenuVisible}
				customActiveStyle={customActiveStyle}
			>
				<Styled.LinkContent>
					<Styled.LinkText variant="button" data-id={navBarTestIds.menuItems}>
						{title}
					</Styled.LinkText>
					{hasSubMenu ? getArrows(subMenuVisible) : null}
				</Styled.LinkContent>
			</NavigationLink>
			{subMenuVisible && hasSubMenu ? (
				<SubMenu
					parentIdentifier={parentId}
					data-id={navBarTestIds.navMenuSubMenu}
					subpages={subpages}
					onClickOutside={handleMenuClick}
					onClickInside={hideSubMenu}
					outsideClickIgnoreClass={IGNORE_ON_CLICK_OUTSIDE_CLASS}
					parentRef={listRef.current}
					analyticsIdentifier={analyticsIdentifier}
					preventDefault={false}
					shouldAlignRight={showSubMenuAlignedRight}
				/>
			) : null}
		</Styled.Li>
	);
};

type NavigationLinkProps = {
	title: string;
	relativePath: string;
	absolutePath?: string;
	className?: string;
	subMenuVisible: boolean;
	hasSubMenu?: boolean;
	customActiveStyle: string;
	children: ReactNode;
	handleMenuClick: (event: any) => void;
};

type Ref = HTMLHeadElement;

const NavigationLink = forwardRef<Ref, NavigationLinkProps>((props, ref) => {
	const {
		children,
		absolutePath,
		relativePath,
		className,
		subMenuVisible,
		hasSubMenu,
		title,
		customActiveStyle,
		handleMenuClick,
	} = props;

	const NavLink = relativePath ? RouterNavLink : AnchorNavLink;

	function getUrl() {
		let link: any;
		if (hasSubMenu) {
			link = '#';
		}

		if (title == 'Home') {
			link = '/';
		} else {
			link = relativePath ? relativePath : absolutePath;
		}

		return link;
	}

	return (
		<NavLink
			forwardedRef={ref}
			{...{ [relativePath ? 'to' : 'href']: getUrl() }}
			className={`${className} ${
				subMenuVisible ? IGNORE_ON_CLICK_OUTSIDE_CLASS : ''
			}`}
			onClick={handleMenuClick}
			onKeyDown={(event: any) => {
				if (event.keyCode === 9 && event.shiftKey && subMenuVisible) {
					handleMenuClick(!subMenuVisible);
				}
			}}
			aria-expanded={subMenuVisible ? 'true' : 'false'}
			showRaisedLink={hasSubMenu && subMenuVisible}
			isActive={title === 'Home'}
			style={customActiveStyle}
			data-id={navBarTestIds.navMenuItemLink}
			data-analytics-enabled={hasSubMenu ? false : true}
		>
			{children}
		</NavLink>
	);
});

NavigationLink.displayName = 'NavLink';

export default MenuItem;
